/**
 * DashboardSessionTabs — Tabbed navigation for the main Dashboard.
 * Each tab maps to a named session (CRM, Collection, Activities, Performance).
 * Admins can add/rename/delete tabs and link them to card templates.
 */
import React, { useState, useEffect, useRef } from 'react';
import { targetAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Target, DollarSign, Activity, TrendingUp, LayoutGrid, Plus, Pencil,
  X, BarChart2, Users, ShoppingCart, CheckCircle, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  Target, DollarSign, Activity, TrendingUp, LayoutGrid, BarChart2,
  Users, ShoppingCart, CheckCircle, Zap,
};

const ICON_OPTIONS = [
  { value: 'Target',    label: 'Target' },
  { value: 'DollarSign',label: 'Dollar Sign' },
  { value: 'Activity',  label: 'Activity' },
  { value: 'TrendingUp',label: 'Trending Up' },
  { value: 'BarChart2', label: 'Bar Chart' },
  { value: 'Users',     label: 'Users' },
  { value: 'ShoppingCart', label: 'Shopping Cart' },
  { value: 'LayoutGrid', label: 'Grid' },
];

const COLOR_OPTIONS = [
  { value: '#800000', label: 'Maroon' },
  { value: '#059669', label: 'Green' },
  { value: '#2563EB', label: 'Blue' },
  { value: '#7C3AED', label: 'Purple' },
  { value: '#D97706', label: 'Amber' },
  { value: '#DB2777', label: 'Pink' },
  { value: '#0891B2', label: 'Teal' },
];

export function DashboardSessionTabs({ activeTab, onTabChange, onSessionsLoaded, isAdmin }) {
  const [sessions, setSessions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [form, setForm] = useState({ name: '', icon: 'LayoutGrid', color: '#800000', template_id: '' });

  useEffect(() => {
    loadSessions();
    targetAPI.listTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  const loadSessions = async () => {
    try {
      const res = await targetAPI.listSessions();
      const tabs = res.data || [];
      setSessions(tabs);
      onSessionsLoaded?.(tabs);           // ← pass full list to parent (first load)
      if (!activeTab && tabs.length > 0) {
        onTabChange?.(tabs[0].id);        // auto-select first tab
      }
    } catch {}
  };

  // Only call onSessionsLoaded once when sessions first load
  const loadedRef = useRef(false); // eslint-disable-line

  const handleAdd = async () => {
    try {
      await targetAPI.createSession({
        name: form.name, icon: form.icon, color: form.color,
        template_id: form.template_id || null, order: sessions.length,
      });
      toast.success(`Tab "${form.name}" created`);
      setAddOpen(false);
      setForm({ name: '', icon: 'LayoutGrid', color: '#800000', template_id: '' });
      await loadSessions();
    } catch { toast.error('Failed to create tab'); }
  };

  const handleEdit = async () => {
    try {
      await targetAPI.updateSession(editSession.id, {
        name: form.name, icon: form.icon, color: form.color,
        template_id: form.template_id || null, order: editSession.order || 0,
      });
      toast.success(`Tab "${form.name}" updated`);
      setEditOpen(false);
      await loadSessions();
    } catch { toast.error('Failed to update tab'); }
  };

  const handleDelete = async (session) => {
    if (!window.confirm(`Delete tab "${session.name}"?`)) return;
    try {
      await targetAPI.deleteSession(session.id);
      toast.success(`Tab "${session.name}" deleted`);
      await loadSessions();
      if (activeTab === session.id && sessions.length > 1) {
        const remaining = sessions.filter(s => s.id !== session.id);
        onTabChange?.(remaining[0]?.id);
      }
    } catch { toast.error('Failed to delete tab'); }
  };

  const openEdit = (s) => {
    setEditSession(s);
    setForm({ name: s.name, icon: s.icon || 'LayoutGrid', color: s.color || '#800000', template_id: s.default_template_id || s.template_id || '' });
    setEditOpen(true);
  };

  return (
    <>
      {/* Tab bar — matches the reference design: clean underline + bg on active */}
      <div className="flex items-end gap-0 border-b bg-background" data-testid="dashboard-session-tabs">
        {sessions.map(s => {
          const Icon = ICON_MAP[s.icon] || LayoutGrid;
          const isActive = s.id === activeTab;
          const tabColor = s.color || '#800000';
          return (
            <div key={s.id} className="group relative">
              <button
                onClick={() => onTabChange?.(s.id)}
                className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap select-none ${
                  isActive
                    ? 'text-gray-900 bg-white'
                    : 'text-muted-foreground hover:text-gray-700 hover:bg-gray-50/70'
                }`}
                data-testid={`session-tab-${s.id}`}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: isActive ? tabColor : undefined }} />
                <span style={{ color: isActive ? tabColor : undefined }}>{s.name}</span>
                {/* Active indicator — thick colored underline */}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-sm"
                    style={{ backgroundColor: tabColor }}
                  />
                )}
              </button>
              {/* Edit button on hover (admin only) */}
              {isAdmin && (
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                  className="absolute top-1.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-100 z-10"
                  title="Edit tab"
                >
                  <Pencil className="h-2.5 w-2.5 text-gray-400" />
                </button>
              )}
            </div>
          );
        })}
        {isAdmin && (
          <button
            onClick={() => { setForm({ name: '', icon: 'LayoutGrid', color: '#800000', template_id: '' }); setAddOpen(true); }}
            className="flex items-center gap-1 px-3 py-3 text-xs text-muted-foreground hover:text-gray-600 hover:bg-gray-50 transition-all"
            data-testid="add-session-tab-btn"
          >
            <Plus className="h-3.5 w-3.5" /><span>Tab</span>
          </button>
        )}
      </div>

      {/* Add Tab Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Add Dashboard Tab</DialogTitle>
          </DialogHeader>
          <SessionForm form={form} setForm={setForm} templates={templates} />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-[#800000] hover:bg-[#9a1919] text-white"
              onClick={handleAdd} disabled={!form.name.trim()}>
              Create Tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tab Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center justify-between">
              Edit Tab
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500 hover:bg-red-50"
                onClick={() => { setEditOpen(false); if (editSession) handleDelete(editSession); }}>
                <X className="h-3 w-3 mr-1" />Delete
              </Button>
            </DialogTitle>
          </DialogHeader>
          <SessionForm form={form} setForm={setForm} templates={templates} />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-[#800000] hover:bg-[#9a1919] text-white"
              onClick={handleEdit} disabled={!form.name.trim()}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Session form fields ───────────────────────────────────────────────────────
function SessionForm({ form, setForm, templates }) {
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const Icon = ICON_MAP[form.icon] || LayoutGrid;

  return (
    <div className="space-y-3 py-1">
      <div>
        <label className="text-xs font-semibold mb-1.5 block">Tab Name *</label>
        <Input value={form.name} onChange={e => u('name', e.target.value)}
          placeholder="e.g. CRM, Finance, Performance..." className="text-sm h-9" autoFocus data-testid="session-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold mb-1.5 block">Icon</label>
          <Select value={form.icon} onValueChange={v => u('icon', v)}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue>
                <span className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5" />
                  {ICON_OPTIONS.find(i => i.value === form.icon)?.label || form.icon}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ICON_OPTIONS.map(opt => {
                const OIcon = ICON_MAP[opt.value] || LayoutGrid;
                return <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  <span className="flex items-center gap-2"><OIcon className="h-3.5 w-3.5" />{opt.label}</span>
                </SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold mb-1.5 block">Color</label>
          <Select value={form.color} onValueChange={v => u('color', v)}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: form.color }} />
                  {COLOR_OPTIONS.find(c => c.value === form.color)?.label || form.color}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {COLOR_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: opt.value }} />
                    {opt.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold mb-1.5 block">Fallback Template (optional)</label>
        <Select value={form.template_id || '__none__'} onValueChange={v => u('template_id', v === '__none__' ? '' : v)}>
          <SelectTrigger className="h-9 text-xs" data-testid="session-fallback-template-select"><SelectValue placeholder="No fallback template linked" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="text-xs">No template</SelectItem>
            {templates.map(t => (
              <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground mt-1">Role-specific templates override this automatically. Use this only as the shared fallback for the tab.</p>
      </div>
    </div>
  );
}
